import type { DepartureDateFilterOptions } from '@/scopes/search/components/DepartureDateFilterLegacy/types'
import { useIntl } from '@/intl'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import showWeeks from '@/scopes/search/domain/showWeeks'
import {
  getDaysAround,
  mqMin,
  removePastDatesFromRange,
  space,
  tokPx,
} from '@sunwebgroup/ui-components'
import { DatePicker, MonthPicker } from '@sunwebgroup/ui-components/uds'
import { add } from 'date-fns'
import * as React from 'react'
import { styled } from 'styled-components'
import { useDepartureDateFilter } from './hooks'

const StyledDatePicker = styled(DatePicker)`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${DatePicker.modSize('tablet')}
  }

  @media ${mqMin('primitives_breakpoint_l_min')}{
    ${DatePicker.modSize('desktop')}
  }
`

const StyledMonthPicker = styled(MonthPicker)`

  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-bottom: ${space('primitives_space_32')};
    margin-top: ${space('primitives_space_48')};
    width: calc(100dvw - 50px);
  }

  @media ${mqMin('primitives_breakpoint_l_min')} {
    width: ${tokPx('primitives_area_size_720')};
  }
`

export function DatesCalendar({ options, className }: { options: DepartureDateFilterOptions, className?: string }) {
  const intl = useIntl()
  const { isDesktopMd } = useBreakpoints()
  const siteName = useConfiguration('siteName')

  const { availableDates, departureDate, flexibleRange, originDate, setOriginDate, setDepartureDate }
    = useDepartureDateFilter({ options })

  function setSelectedDate(date: string) {
    setOriginDate(date)

    if (flexibleRange !== 'exact') {
      const numberOfDays = Number.parseInt(flexibleRange)
      const dateRange = getDaysAround(date, numberOfDays)
      const minimDepartureDate = add(new Date(), { days: 1 })
      const filteredDates = removePastDatesFromRange(dateRange, minimDepartureDate)
      setDepartureDate(filteredDates)
      return
    }

    setDepartureDate(date)
  }

  React.useEffect(() => {
    if (!originDate && departureDate) {
      setOriginDate(Array.isArray(departureDate) ? departureDate?.[0] : departureDate)
    }
  }, [])

  const showWeekNumbers = showWeeks(siteName)

  return (
    <StyledDatePicker
      className={className}
      availableDates={availableDates}
      selectedDate={originDate ?? (Array.isArray(departureDate) ? departureDate?.[0] : departureDate)}
      startDate={Array.isArray(departureDate) ? departureDate[0] : undefined}
      endDate={Array.isArray(departureDate) ? departureDate[1] : undefined}
      locale={intl.locale}
      setSelectedDate={setSelectedDate}
      nextMonthAriaLabel={intl.formatMessage({ id: 'quicksearch_filter_departure_date_next_month' })}
      previousMonthAriaLabel={intl.formatMessage({ id: 'quicksearch_filter_departure_date_previous_month' })}
      numberOfMonths={isDesktopMd ? 2 : 1}
      showWeekNumbers={showWeekNumbers}
      weekLabel={showWeekNumbers ? intl.formatMessage({ id: 'departure_date_filter_week' }) : ''}
    />
  )
}

interface MonthCalendarProps { options: DepartureDateFilterOptions, name: string, className?: string }

export function MonthCalendar({ options, name, className }: MonthCalendarProps) {
  const intl = useIntl()
  const { availableDates, maxDateString, minDateString, departureDate, setDepartureDate, setOriginDate }
    = useDepartureDateFilter({
      options,
    })

  function setSelectedDate(dates: string | [string, string]) {
    setOriginDate(Array.isArray(dates) ? dates[0] : dates)
    setDepartureDate(dates)
  }

  return (
    <StyledMonthPicker
      name={name}
      locale={intl.locale}
      availableDates={availableDates}
      maxDateString={maxDateString}
      minDateString={minDateString}
      selectedMonth={departureDate}
      setSelectedDates={setSelectedDate}
      className={className}
    />
  )
}
